import { IUser } from "services/types";
import { deviceTypeEnum } from "hooks/useDeviceType";

export enum ViewRegion {
  Collections = "collections",
  Embed = "embed",
  Explore = "explore",
  Favourites = "favourites",
  ForYou = "foryou",
  Main = "main",
  Niche = "niche",
  Other = "other",
  Profile = "profile",
  Related = "related",
  Search = "search",
}

export interface GifViewEvent {
  gifId: string;
  tags: string[];
  source: ViewRegion;
  position: number;
  boosted: boolean;
  userName?: string | null;
  fullscreen: boolean;
}

export interface MeaningfulGifViewEvent {
  gifId: string;
}

export interface GifPlaybackEvent {
  duration: number;
  gif: string;
  context: string | null;
  userName?: string | null;
  percent: number;
}

export interface UploadSelectedEvent {
  type: "image" | "video" | "bulkImages";
}

export interface UploadTagSelectionEndedEvent {
  count: number;
}

export interface GifClickedEvent {
  source: ViewRegion;
  position: number;
}

export interface GifLikedEvent {
  gifId: string;
}

export interface GifUnlikedEvent {
  gifId: string;
}

export interface AddedToCollectionEvent {
  gifId: string;
}

export interface AdLoadedEvent {
  isLoaded: boolean;
}

export interface MenuButtonClickedEvent {
  name: string;
  label: string;
}

export interface FollowUserClickedEvent {
  userName: string;
}

export interface SocialProfileClickedEvent {
  userName: string;
}

export interface TagClickedEvent {
  tag: string;
}

export interface SearchedEvent {
  query: string;
  totalCount: number;
}

export interface UploadFinishedEvent {
  sourceURL?: "local" | "url";
}

export interface ProfileViewedEvent {
  userName: string;
}

export interface UserFetchedEvent {
  user: IUser;
}

export interface ExperimentVariantExposureEvent {
  experimentKey: string;
  experimentVariant: string | boolean;
}

export interface FirstPageVisitedEvent {
  path: string;
}

export interface DeviceTrackedEvent {
  deviceId: string;
  deviceType: deviceTypeEnum;
}

export type BusEvents = {
  gifView: GifViewEvent;
  meaningfulGifView: MeaningfulGifViewEvent;
  gifPlayback: GifPlaybackEvent;

  forceFullscreenEntered: undefined;
  forceFullscreenExited: undefined;
  fullscreenEntered: undefined;
  fullscreenExited: undefined;

  uploadSelected: UploadSelectedEvent;
  uploadStarted: undefined;
  uploadTagSelectionStarted: undefined;
  uploadTagSelectionEnded: UploadTagSelectionEndedEvent;
  uploadEnded: undefined;

  watchPageVisited: undefined;
  firstPageVisited: FirstPageVisitedEvent;

  gifClicked: GifClickedEvent;
  gifLiked: GifLikedEvent;
  gifUnliked: GifUnlikedEvent;
  addedToCollection: AddedToCollectionEvent;

  adLoaded: AdLoadedEvent;

  infoBarClicked: undefined;
  infoBarClosed: undefined;

  sharedFixedEmbed: undefined;
  sharedResponsiveEmbed: undefined;
  shareClick: undefined;
  shareCopy: undefined;
  shareDiscord: undefined;
  shareEmbed: undefined;
  shareReddit: undefined;
  shareTwitter: undefined;
  optionClicked: undefined;
  pinnedContentClicked: undefined;
  menuButtonClicked: MenuButtonClickedEvent;
  followUserClicked: FollowUserClickedEvent;
  socialProfileClicked: SocialProfileClickedEvent;
  tagClicked: TagClickedEvent;

  signUpViewed: undefined;
  signUpSuccess: undefined;
  loginCompleted: undefined;

  scrollPromptViewed: undefined;
  scrollPromptClosed: undefined;

  searchBarOpened: undefined;
  searched: SearchedEvent;

  uploadVideoTrimmed: undefined;
  uploadGetVerifiedClicked: undefined;
  uploadImageConfirmed: undefined;
  uploadVideoConfirmed: undefined;
  uploadMultipleConfirmed: undefined;
  uploadAudienceDefined: undefined;
  uploadUrlSelected: undefined;
  uploadImagesDropped: undefined;
  uploadImageDropped: undefined;
  uploadVideoDropped: undefined;
  uploadFinished: UploadFinishedEvent;
  uploadEncodingStarted: undefined;
  uploadEncodingFinished: undefined;
  gifWatched120s: undefined;
  profileViewed: ProfileViewedEvent;
  errorLoadingGifs: undefined;
  voteUpAddClicked: undefined;
  voteUpRemoveClicked: undefined;
  voteDownAddClicked: undefined;
  voteDownRemoveClicked: undefined;
  privateSessionStarted: undefined;

  userFetched: UserFetchedEvent;
  deviceTracked: DeviceTrackedEvent;

  experimentVariantExposure: ExperimentVariantExposureEvent;
};
